import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import type { SelectionBookmark, Transaction } from '@tiptap/pm/state';
import type { Mark } from '@tiptap/pm/model';
import { closeHistory } from '@tiptap/pm/history';
import { Dialog } from '../dialogs/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { insideFieldContainer } from './field-container';
import { characterCategories, loadCharacterCatalog, searchCharacters, SpecialCharacter, specialCharacters, unicodeCodePoints } from './special-characters';
import '../../assets/special-character-dialog.css';

export function SpecialCharacterDialog({ editor, request }: { editor: Editor; request: number }) {
    const [visible, setVisible] = useState(false);
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('All');
    const [value, setValue] = useState('');
    const [error, setError] = useState('');
    const [catalog, setCatalog] = useState<SpecialCharacter[] | null>(null);
    const [catalogError, setCatalogError] = useState(false);
    const [retry, setRetry] = useState(0);
    const [limit, setLimit] = useState(96);
    const [preview, setPreview] = useState<SpecialCharacter | null>(null);
    const bookmark = useRef<SelectionBookmark | null>(null);
    const marks = useRef<readonly Mark[]>([]);
    const handled = useRef(0);
    const id = useId();
    const search = query.trim();

    useEffect(() => {
        if (!visible || !search || catalog) return;
        let active = true;
        setCatalogError(false);
        void loadCharacterCatalog().then(items => {
            if (active) setCatalog(items);
        }).catch(() => { if (active) setCatalogError(true); });
        return () => { active = false; };
    }, [visible, search, catalog, retry]);

    useEffect(() => { setLimit(96); setPreview(null); }, [query, category, visible]);

    useEffect(() => {
        setVisible(false);
        bookmark.current = null;
        handled.current = 0;
        const map = ({ transaction }: { transaction: Transaction }) => {
            if (bookmark.current) bookmark.current = bookmark.current.map(transaction.mapping);
        };
        editor.on('transaction', map);
        return () => { editor.off('transaction', map); bookmark.current = null; };
    }, [editor]);

    useEffect(() => {
        if (!request || request === handled.current || editor.isDestroyed || !editor.isEditable || insideFieldContainer(editor)) return;
        handled.current = request;
        bookmark.current = editor.state.selection.getBookmark();
        marks.current = editor.state.storedMarks ?? editor.state.selection.$from.marks();
        setQuery(''); setCategory('All'); setValue(''); setError(''); setVisible(true);
    }, [editor, request]);

    const close = () => { setVisible(false); bookmark.current = null; };
    const insert = () => {
        if (!value || !bookmark.current) return;
        if (editor.isDestroyed || !editor.isEditable) { setError('The editor is no longer available for editing.'); return; }
        try {
            const selection = bookmark.current.resolve(editor.state.doc);
            // Insert a text node so characters such as < and & never become HTML.
            const inserted = editor.chain().setTextSelection({ from: selection.from, to: selection.to })
                .command(({ tr }) => { closeHistory(tr); return true; })
                .insertContent({ type: 'text', text: value, marks: marks.current.map(mark => mark.toJSON()) }).focus().run();
            if (!inserted) throw new Error('Insertion was not allowed.');
            close();
        } catch { setError('Unable to insert here. Close this dialog and select a text position in the editor.'); }
    };
    const filtered = useMemo(() => {
        // Keep the overview small while representing every category.
        if (!search && category === 'All') {
            return characterCategories.flatMap(name => specialCharacters.filter(item => item.category === name).slice(0, 4));
        }
        return searchCharacters(search && catalog ? catalog : specialCharacters, search, category);
    }, [search, category, catalog]);
    const results = search ? filtered.slice(0, limit) : filtered;
    const previewCharacter = preview ?? results[0];
    const loading = !!search && !catalog && !catalogError;

    return <Dialog className="special-character-dialog" header="Insert symbol / special character / emoji" visible={visible} onHide={close}
        modal style={{ width: '40rem', maxWidth: '95vw' }}
        footer={<><Button label="Cancel" outlined onClick={close} /><Button label="Insert" disabled={!value} onClick={insert} /></>}>
        <div className="special-character-picker">
            <div className="special-character-filters">
                <div><label htmlFor={`${id}-search`}>Search characters</label>
                    <InputText id={`${id}-search`} value={query} onChange={event => setQuery(event.target.value)} placeholder="Name, character, or U+ code" autoFocus />
                </div>
                <div><label htmlFor={`${id}-category`}>Category</label>
                    <select id={`${id}-category`} className="p-inputtext" value={category} onChange={event => setCategory(event.target.value)}>
                        {characterCategories.map(name => <option key={name}>{name}</option>)}
                    </select>
                </div>
            </div>
            <strong>{search ? 'Search results' : 'Sample characters'}</strong>
            {!search && <small>This is a small sample. Search for symbols above to see more choices from the full collection.</small>}
            <small>Click characters to add them in order. Click again to add another copy.</small>
            {loading && <p role="status">Loading the full character catalog…</p>}
            {catalogError && search && <div role="alert">The full catalog could not be loaded. Common characters are still available. <Button label="Retry search" text onClick={() => setRetry(current => current + 1)} /></div>}
            {search && catalog && <small role="status">Showing {results.length} of {filtered.length} matches</small>}
            <div className="special-character-grid" role="group" aria-label={search ? 'Search results' : 'Sample characters'} aria-busy={loading}>
                {results.map(item => <button key={item.value} type="button" className="special-character-choice"
                    aria-label={item.name} title={`${item.name} (${unicodeCodePoints(item.value)})`}
                    onMouseEnter={() => setPreview(item)} onFocus={() => setPreview(item)}
                    onClick={() => { setValue(current => current + item.value); setError(''); }}>
                    {item.category === 'Spaces' ? <span className="special-character-choice-name">{item.name}</span>
                        : item.category === 'Combining marks' ? `◌${item.value}` : item.value}
                </button>)}
            </div>
            {!results.length && !loading && !catalogError && <p role="status">{search ? 'No matching characters. You can enter a character below.' : 'No common characters in this category. Search to find more.'}</p>}
            {search && results.length < filtered.length && <Button label="Show more results" outlined onClick={() => setLimit(current => current + 96)} />}
            {previewCharacter && <div className="special-character-result-preview" aria-label="Character preview">
                <span className="special-character-result-glyph">{previewCharacter.category === 'Spaces' ? '␣'
                    : previewCharacter.category === 'Combining marks' ? `◌${previewCharacter.value}` : previewCharacter.value}</span>
                <span>{previewCharacter.name}<small>{unicodeCodePoints(previewCharacter.value)}</small></span>
            </div>}
            <label htmlFor={`${id}-value`}>Characters to insert</label>
            <InputText id={`${id}-value`} value={value} onChange={event => { setValue(event.target.value); setError(''); }} />
            {value && <div className="special-character-preview" aria-live="polite"><span>{value}</span><small>{unicodeCodePoints(value)}</small></div>}
            {error && <p role="alert">{error}</p>}
        </div>
    </Dialog>;
}
